import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { isSelectable, offeredPresets, presetsFor, type Preset } from "../render/presets.js";
import { ExportChoice, type RenderWhere } from "../render/ExportChoice.js";
import { safeValidateTimeline, serverRenderPrice, timelineDuration, type Eta, type Timeline } from "@veyso/core";
import { AppShell } from "../components/AppShell.js";
import { ErrorNote, Eyebrow, timecode } from "../components/ui.js";
import { formatUsd } from "@veyso/core";
import { ApiError, api, type LanguagesView, type RenderStatus, type ExportQuote } from "../api.js";
import { previewTimeline } from "../editor/languageView.js";
import { ExportRows } from "../render/ExportRows.js";
import { ExportPanel } from "../render/ExportPanel.js";
import { DownloadTab } from "../render/DownloadTab.js";
import { ExportHistory } from "../render/ExportHistory.js";
import { exportMeta, qualityChoices, tabFromQuery, videoLength, type ExportTab } from "../render/exportView.js";
import { blockedMessage } from "../languages/languages.js";
import { ExportPlayer } from "../render/ExportPlayer.js";
import { ShareSection } from "../share/ShareSection.js";
import { ProjectNav } from "../components/ProjectNav.js";
import { shareRefreshKey } from "../share/shareView.js";
import {
  exportCountLabel,
  exportQueue,
  cannotRenderNotice,
  exportRows,
  initialChecked,
  nextExport,
  playerChoices,
  startExport,
  stopExport,
  type ExportOutcome,
  type ExportState,
  type RowRun,
} from "../render/languageExports.js";
import { capture } from "../lib/posthog.js";
import { ProgressLine } from "../components/ProgressLine.js";
import { BROWSER_NOTE, browserEta, emailNoteFor, serverRenderLabel } from "../lib/eta.js";
import { useJobActivity } from "../lib/jobActivity.js";
import { useAccount } from "../auth/Gate.js";

/** Mirrors the worker's retention: shown so the list does not look like a bug. */
const EXPORTS_KEPT = 5;

/**
 * What the export button says, matched to the progress box below it.
 *
 * A render in this tab runs entirely inside the button's starting state, so
 * reading only `starting` called a render that was already ten percent done
 * "Queueing…" — while the box under it said "Rendering your video". Nothing is
 * queued in a browser render, so the browser's own state is asked first.
 */
export function exportButtonLabel(state: {
  running: boolean;
  starting: boolean;
  here: { uploading: boolean } | null;
}): string {
  if (state.here) return state.here.uploading ? "Saving…" : "Rendering…";
  if (state.running) return "Exporting…";
  if (state.starting) return "Queueing…";
  return "Export video";
}

/**
 * The export page, which now watches two quite different things.
 *
 * A render on our servers is a queued job: this page is a live view of it,
 * closing the tab does not stop it, and coming back picks the status straight
 * back up. A render in this tab has no existence outside it — no job row,
 * nothing to resume — so closing the tab throws the work away.
 *
 * That difference is why the destination is asked rather than chosen: it
 * changes what the person exporting can do for the next few minutes, and only
 * they know whether that matters.
 */
export const Render: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();

  const [timeline, setTimeline] = useState<Timeline | null>(null);
  /** Whether the share is live; null until the Share tab has read it. */
  const [shareLive, setShareLive] = useState<boolean | null>(null);
  const [preset, setPreset] = useState(0);
  const [presetChosen, setPresetChosen] = useState(false);
  /**
   * Whether the export as currently set up will happen in this tab.
   *
   * A different question from `canEncode4K`, which decides what to *offer* and
   * is asked once. This one decides whether the render is free, so it has to
   * follow the preset — and it asks the full question, timeline included,
   * because a blur this browser draws wrong sends the whole export to a worker
   * however well it could have encoded the pixels.
   *
   * Null until it has been asked. The copy has three branches for that reason:
   * saying "free" and then correcting it is worse than saying "if".
   */
  const [canEncodeChosen, setCanEncodeChosen] = useState<boolean | null>(null);
  /** Why not, in the dialog's words rather than a shrug. */
  const [cannotEncodeBecause, setCannotEncodeBecause] = useState<string[]>([]);
  /** Whether the "where should this render?" dialog is up. */
  const [choosing, setChoosing] = useState(false);

  /**
   * Every size, including the two that cannot be picked yet.
   *
   * This used to hide 4K unless the tab could encode it, on the reasoning that
   * a greyed-out button invites "why not?". It does — and the version where it
   * is simply absent invites the same question with no way to ask it, while
   * showing a different list on two laptops of the same age. "Coming soon"
   * answers it in two words and happens to be true.
   */
  const presets = offeredPresets(timeline);
  const [renderId, setRenderId] = useState<string | null>(null);
  const [status, setStatus] = useState<RenderStatus | null>(null);
  const account = useAccount();
  // Whether this export's job will email the person looking.
  const { active: jobs } = useJobActivity();
  const [existing, setExisting] = useState<{
    url: string;
    downloadUrl: string;
    filename: string | null;
    sizeBytes: number;
  } | null>(null);
  /** The recording's own size, which decides whether a bigger export gains anything. */
  // Width and height warn about an upscale; the rest is what makes the original
  // recording downloadable. It is the one file a project holds that the user
  // put in rather than got out, and until now there was no way to get it back.
  const [source, setSource] = useState<{
    key: string;
    filename: string;
    sizeBytes: number;
    durationSeconds: number | null;
    width: number | null;
    height: number | null;
  } | null>(null);
  /** The recording as an MP4, when it arrived as something a Mac cannot open (the extension's WebM). */
  const [original, setOriginal] = useState<typeof source>(null);
  const raw = original ?? source;
  const [exports, setExports] = useState<Awaited<ReturnType<typeof api.exports>>>([]);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * The video's languages, each exported as its own MP4 in this tab.
   *
   * Null until read, and when the read fails: the English export never waits
   * on this, so a languages route that does not answer only leaves the English row.
   */
  const [languages, setLanguages] = useState<LanguagesView | null>(null);
  const languagesRef = useRef<LanguagesView | null>(null);
  /** The rows checked for the next export, by `rowKey`: the English is "". */
  const [checked, setChecked] = useState<string[]>([""]);
  const seeded = useRef(false);
  const [searchParams] = useSearchParams();
  const askedLanguage = searchParams.get("lang");
  /** Which of the side panel's tabs is open; `?tab=share` opens Share. */
  const [tab, setTab] = useState<ExportTab>(() => tabFromQuery(searchParams.get("tab")));
  /** How far each language row of the running export has got. */
  const [runs, setRuns] = useState<Record<string, RowRun>>({});
  const [languagesRunning, setLanguagesRunning] = useState(false);
  const languageAbort = useRef<AbortController | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      // A language render lives in this tab; leaving the page ends it.
      languageAbort.current?.abort();
    };
  }, []);

  const loadLanguages = useCallback(
    () =>
      api
        .languages(id)
        .then((view) => {
          languagesRef.current = view;
          if (!alive.current) return;
          setLanguages(view);
          // Opened for a language (`?lang=de`): only that one starts checked, once.
          if (!seeded.current) {
            seeded.current = true;
            setChecked(initialChecked(exportRows(view), askedLanguage));
          }
        })
        .catch(() => {
          // Not offered rather than an error: the English export does not need it.
        }),
    [id, askedLanguage],
  );
  useEffect(() => {
    void loadLanguages();
  }, [loadLanguages]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await api.timeline(id);
        const parsed = safeValidateTimeline(response.data);
        if (parsed.success) setTimeline(parsed.data);
      } catch (cause) {
        setError((cause as Error).message);
      }
      try {
        const project = await api.getProject(id);
        setSource(project.assets.find((asset) => asset.kind === "source") ?? null);
        setOriginal(project.assets.find((asset) => asset.kind === "original") ?? null);
      } catch {
        // Only used to warn about an upscale; not worth an error on this page.
      }
      try {
        setExisting(await api.output(id));
        setExports(await api.exports(id).catch(() => []));
      } catch {
        // No previous render is the normal first-time case.
      }
      try {
        // Adopt a render that is already running. It was started by some tab —
        // possibly this one before a reload, possibly another — and the id was
        // the only way to follow it. Without this the bar disappeared on
        // refresh while the render carried on, and the page offered to start a
        // second one on top of it.
        const running = await api.currentRender(id);
        if (running) setRenderId(running.id);
      } catch {
        // Nothing running is the common case, and not worth an error here.
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!renderId) return;

    const poll = async () => {
      try {
        const next = await api.renderStatus(id, renderId);
        setStatus(next);
        if (next.status === "completed" || next.status === "failed" || next.status === "cancelled") {
          if (pollRef.current) clearInterval(pollRef.current);
          // Re-read rather than assembling it from the status payload: the
          // output route is what knows the download URL and the filename, and
          // one request on completion is cheaper than teaching two routes to
          // agree about them.
          if (next.output) setExisting(await api.output(id).catch(() => null));
        }
      } catch (cause) {
        setError((cause as Error).message);
      }
    };

    void poll();
    pollRef.current = setInterval(() => void poll(), 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id, renderId]);

  /**
   * Stops the render.
   *
   * The worker notices on its next frame and puts the project back where it
   * was, so there is nothing to undo here — the poll above reports the new
   * status within a couple of seconds and the screen follows it.
   */
  const cancel = async () => {
    if (!renderId) return;
    setCancelling(true);
    setError(null);
    try {
      await api.cancelRender(id, renderId);
      capture("export_cancel_requested", { project_id: id });
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setCancelling(false);
    }
  };

  /**
   * What this export costs, before it is started.
   *
   * The pricing page says the cost is shown before anything is processed, and
   * by this point it is knowable exactly: every provider call a video needs has
   * already happened during processing, and rendering spends nothing with any
   * of them. So this is the price, not an estimate of it.
   *
   * Re-fetched whenever an export finishes, because the first one on a project
   * is what makes every later one free — leaving the old quote on screen would
   * go on asking for money for something already paid for.
   */
  const [quote, setQuote] = useState<ExportQuote | null>(null);

  const loadQuote = useCallback(() => {
    void api
      .exportQuote(id)
      .then(setQuote)
      .catch(() => {
        // A screen that cannot price the export still exports. The server
        // refuses it if it cannot be afforded; this is the courtesy, not the
        // control.
      });
  }, [id]);

  useEffect(loadQuote, [loadQuote]);

  /**
   * Where this export is happening, for the screen to say so.
   *
   * Named rather than inferred from which spinner is turning. Somebody whose
   * export is running in their own tab needs to know that closing it stops
   * the render, and somebody whose is on a worker needs to know it does not.
   */
  const [here, setHere] = useState<{ progress: number; uploading: boolean; eta: Eta } | null>(null);
  /** When this tab's export started: its time left is smoothed under its own key, so a stopped run's countdown is not the next one's. */
  const [browserRun, setBrowserRun] = useState(0);
  const browserAbort = useRef<AbortController | null>(null);

  /**
   * Renders in this tab, if this tab can.
   *
   * Returns false when it cannot even start, which is the caller's signal to
   * use a worker. A failure *during* the render returns false too: half a
   * video is no video, and the server path still produces one.
   */
  const renderHere = async (chosen: Preset): Promise<boolean> => {
    if (!timeline) return false;

    let modules;
    try {
      modules = await Promise.all([import("../render/webRender.js"), import("@veyso/core")]);
    } catch (cause) {
      // A tab left open across a deploy, asking for a chunk that is gone. The
      // preload handler in main.tsx reloads it.
      capture("export_browser_failed", { reason: (cause as Error).message });
      return false;
    }
    const [{ renderInBrowser, canRenderHere }, { computeLayout, outputSize }] = modules;
    const layout = computeLayout(timeline, chosen.fps);
    // The preset's size, which `computeLayout` knows nothing about — it takes
    // a frame rate and reads the rest off the timeline. Passing the layout
    // straight through is how a 4K export came out 1080p.
    const size = outputSize(timeline, chosen);

    const verdict = await canRenderHere(timeline, size);
    if (!verdict.possible) {
      capture("export_browser_declined", { reasons: verdict.reasons.join("; ") });
      return false;
    }

    const controller = new AbortController();
    browserAbort.current = controller;
    setBrowserRun(Date.now());
    setHere({ progress: 0, uploading: false, eta: { kind: "unknown" } });

    try {
      const { urls } = await api.assetUrls(id);
      const rendered = await renderInBrowser({
        timeline,
        layout,
        output: size,
        assetUrls: urls,
        signal: controller.signal,
        onProgress: ({ progress, estimatedMsRemaining }) =>
          setHere({ progress, uploading: false, eta: browserEta(estimatedMsRemaining) }),
      });

      setHere({ progress: 1, uploading: true, eta: { kind: "unknown" } });
      const output = await api.submitBrowserRender(
        id,
        rendered.blob,
        {
          width: size.width,
          height: size.height,
          fps: layout.fps,
          durationSeconds: layout.totalFrames / layout.fps,
        },
        (fraction) => setHere({ progress: fraction, uploading: true, eta: { kind: "unknown" } }),
      );

      capture("export_finished_in_browser", {
        project_id: id,
        output_width: size.width,
        output_height: size.height,
        seconds: Math.round(rendered.elapsedMs / 1000),
        fps: Math.round(rendered.fps),
      });

      setExisting({
        url: output.url,
        downloadUrl: output.downloadUrl,
        filename: output.filename,
        sizeBytes: output.sizeBytes,
      });
      loadQuote();
      return true;
    } catch (cause) {
      // Deliberately swallowed into a `false`. The worker can still make this
      // video, and telling somebody their export failed when a second way to
      // make it is sitting right there would be giving up on their behalf.
      capture("export_browser_failed", { reason: (cause as Error).message });
      return false;
    } finally {
      browserAbort.current = null;
      setHere(null);
    }
  };

  /**
   * Leaving mid-render loses it.
   *
   * A browser render has no existence outside this tab — no job row, nothing
   * to resume — so closing the tab throws the work away. Nothing has been
   * charged at that point, because credit is only taken once an output is
   * registered, so the cost is time rather than money. Still worth a prompt:
   * twenty minutes lost to a stray Cmd-W is a bad surprise.
   */
  useEffect(() => {
    if (!here) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [here]);

  /**
   * Starts the export where the person exporting said to.
   *
   * No fallback between the two any more, and that is the point. Falling back
   * to the servers when a browser render failed was fine while both were free;
   * it stopped being fine when one of them started costing money, because a
   * silent fallback that charges somebody is a bill they did not agree to.
   *
   * So a failed browser render says so and leaves them on this screen, where
   * the choice is one click away and theirs to make again.
   */
  const start = async (where: RenderWhere) => {
    setStarting(true);
    setError(null);
    try {
      const chosen = presetsFor(timeline)[preset];

      if (where === "browser") {
        if (await renderHere(chosen)) return;
        setError("That render did not finish in your browser. You can try again, or render it on our servers instead.");
        return;
      }

      const job = await api.render(id, { width: chosen.width, height: chosen.height, fps: chosen.fps });
      capture("export_started", {
        project_id: id,
        output_width: chosen.width,
        output_height: chosen.height,
        output_fps: chosen.fps,
      });
      setRenderId(job.id);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setStarting(false);
    }
  };

  /**
   * One language's video, rendered in this tab and saved as that language's
   * MP4 — free, with no job and no charge. Languages are never rendered on our
   * servers, so a browser that cannot render here says so on the row.
   *
   * Read from the latest view rather than the one the export started with: an
   * earlier row's save reloads it, and a language that changed since is not
   * the one that was checked. Says only how it ended; what the row says then
   * is `nextExport`'s.
   */
  const renderLanguage = async (code: string, chosen: Preset, signal: AbortSignal): Promise<ExportOutcome> => {
    const setRun = (run: RowRun) => {
      if (alive.current) setRuns((current) => ({ ...current, [code]: run }));
    };
    const language = languagesRef.current?.languages.find((one) => one.code === code);
    // Out of date is still ready and paid for: it is exported as it is.
    if (!timeline || !language || language.status !== "ready") return "changed";

    try {
      const [{ renderInBrowser, canRenderHere }, { computeLayout, outputSize }] = await Promise.all([
        import("../render/webRender.js"),
        import("@veyso/core"),
      ]);
      const picture = previewTimeline(timeline, language);
      const layout = computeLayout(picture, chosen.fps);
      const size = outputSize(picture, chosen);
      const verdict = await canRenderHere(picture, size);
      if (!verdict.possible) {
        capture("export_language_declined", { reasons: verdict.reasons.join("; ") });
        return "cannot";
      }

      setRun({ phase: "rendering", progress: 0 });
      const { urls } = await api.assetUrls(id);
      const rendered = await renderInBrowser({
        timeline: picture,
        layout,
        output: size,
        assetUrls: urls,
        signal,
        onProgress: ({ progress }) => setRun({ phase: "rendering", progress }),
      });

      setRun({ phase: "saving", progress: 0 });
      await api.submitBrowserRender(
        id,
        rendered.blob,
        {
          width: size.width,
          height: size.height,
          fps: layout.fps,
          durationSeconds: layout.totalFrames / layout.fps,
          language: code,
          // What was rendered: a language that changed meanwhile is refused.
          rendered: language.revision,
        },
        (fraction) => setRun({ phase: "saving", progress: fraction }),
      );
      capture("export_language_finished", {
        project_id: id,
        language: code,
        seconds: Math.round(rendered.elapsedMs / 1000),
      });
      return "done";
    } catch (cause) {
      capture("export_language_failed", { project_id: id, language: code, reason: (cause as Error).message });
      if (cause instanceof ApiError && cause.status === 409) return "changed";
      return (cause as Error).name === "AbortError" ? "stopped" : "failed";
    } finally {
      // "Exported" comes from the view, and so does any change a 409 reported.
      await loadLanguages();
    }
  };

  /**
   * Exports the checked rows one after another: the English the way it always
   * has been — where its customer chose, with its own charge rules — then each
   * language in this tab. A server render of the English runs on a worker, so
   * the languages start as soon as it is queued rather than after it.
   */
  const exportChecked = async (where: RenderWhere | null) => {
    const chosen = presetsFor(timeline)[preset];
    const codes = queue.filter((key) => key !== "");
    const controller = new AbortController();
    languageAbort.current = controller;
    let state: ExportState = startExport(codes);
    setRuns(state.runs);
    setLanguagesRunning(codes.length > 0);
    try {
      if (where) await start(where);
      while (state.queue.length > 0 && alive.current) {
        if (controller.signal.aborted) {
          state = stopExport(state);
        } else {
          const outcome = await renderLanguage(state.queue[0]!, chosen, controller.signal);
          state = nextExport(state, outcome);
        }
        if (alive.current) setRuns(state.runs);
      }
    } finally {
      if (languageAbort.current === controller) languageAbort.current = null;
      if (alive.current) setLanguagesRunning(false);
    }
  };

  // A language render lives in this tab, and leaving loses it.
  useEffect(() => {
    if (!languagesRunning) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [languagesRunning]);

  // The first export of a project is what makes every later one free, so the
  // quote is stale the moment one finishes.
  useEffect(() => {
    if (status?.status === "completed") loadQuote();
  }, [status?.status, loadQuote]);

  const running = status?.status === "queued" || status?.status === "processing";
  const chosen = presetsFor(timeline)[preset];

  /**
   * What this render would cost on our machines, if it goes there.
   *
   * Computed here rather than fetched: every input is already on this screen,
   * and `serverRenderPrice` is the same function the worker charges with, so
   * the figure shown and the figure taken cannot drift.
   */
  useEffect(() => {
    if (!timeline) return;
    let cancelled = false;
    const option = presetsFor(timeline)[preset];

    void (async () => {
      const [{ canRenderHere }, { outputSize }] = await Promise.all([
        import("../render/webRender.js"),
        import("@veyso/core"),
      ]);
      const verdict = await canRenderHere(timeline, outputSize(timeline, option));
      if (cancelled) return;
      setCanEncodeChosen(verdict.possible);
      setCannotEncodeBecause(verdict.reasons);
    })().catch(() => {
      // A stale chunk after a deploy, which main.tsx reloads for. Until then
      // the answer stays null, and the copy says "if" rather than guessing.
    });

    return () => {
      cancelled = true;
    };
  }, [timeline, preset]);

  // A free-forever team is charged nothing for machine time either.
  const renderFeeUsd =
    timeline && !(quote?.billing && quote.freeForever)
      ? serverRenderPrice({
          durationSeconds: timelineDuration(timeline),
          width: chosen.width,
          height: chosen.height,
          fps: chosen.fps,
          renderedInBrowser: false,
        })
      : 0;

  // English's state is what this page already knows: whether it has an export.
  const rows = exportRows(languages).map((row) =>
    row.code === null ? { ...row, state: existing ? ("exported" as const) : ("not-exported" as const) } : row,
  );
  // Only what can be exported now, in the order it is made.
  const queue = exportQueue(checked, rows, canEncodeChosen);
  const cannotRender = cannotRenderNotice(rows, askedLanguage, canEncodeChosen);
  const englishChecked = queue.includes("");
  const idleLabel = exportButtonLabel({ running, starting, here });
  const buttonLabel =
    idleLabel !== "Export video" ? idleLabel : languagesRunning ? "Rendering…" : exportCountLabel(queue.length);
  const toggle = (key: string) =>
    setChecked((current) => (current.includes(key) ? current.filter((one) => one !== key) : [...current, key]));
  // The English row follows its own export, wherever it runs.
  const englishRun: RowRun | null = here
    ? { phase: here.uploading ? "saving" : "rendering", progress: here.progress }
    : running && status
      ? { phase: "rendering", progress: status.progress, where: "servers" }
      : null;
  const rowRuns: Record<string, RowRun> = englishRun ? { ...runs, "": englishRun } : runs;
  const quality = qualityChoices(presets);

  // Default to the project's own frame rate. Encoding a 60fps recording at 30
  // silently halves the smoothness of exactly the moments a demo exists to show
  // — typing, scrolling — and the first preset in the list is a poor reason to
  // do that. The user can still pick another; this only sets the starting point.
  useEffect(() => {
    if (!timeline || presetChosen) return;
    const match = presetsFor(timeline).findIndex((option: Preset) => option.fps === timeline.fps);
    if (match >= 0) setPreset(match);
  }, [timeline, presetChosen]);

  return (
    <AppShell
      live={running}
      nav={<ProjectNav projectId={id} kind="demo" current="export" published={shareLive} />}
      breadcrumb={
        <>
          <Link to="/dashboard" className="shrink-0 text-ink-400 transition-colors hover:text-ink-200">
            Projects
          </Link>
          <span className="shrink-0 text-ink-400">/</span>
          <Link
            to={`/projects/${id}/editor`}
            className="min-w-0 truncate text-ink-400 transition-colors hover:text-ink-200"
            title={timeline?.meta.projectName}
          >
            {timeline?.meta.projectName ?? "Project"}
          </Link>
        </>
      }
    >
      <div className="mx-auto flex max-w-[1320px] flex-col gap-5">
        <div className="flex flex-wrap items-end gap-3">
          {/* Its own line on a phone, rather than squeezed beside the buttons. */}
          <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-1">
            <h1 className="text-[28px] font-semibold tracking-[-0.01em] text-ink-100">Export and share</h1>
            <p className="text-[14px] text-ink-300">
              {timeline
                ? exportMeta({
                    duration: videoLength(timelineDuration(timeline)),
                    width: chosen.width,
                    height: chosen.height,
                    fps: chosen.fps,
                    exportedAt: exports[0] ? new Date(exports[0].createdAt) : null,
                    now: new Date(),
                  })
                : " "}
            </p>
          </div>

          {/* The original, as a link rather than a panel of its own.
              `?download` rather than the HTML attribute. The attribute only
              works same-origin, and `/assets/*` is only same-origin on local
              storage — on object storage it answers with a signed URL on
              another host, where the attribute is ignored and the browser
              opens the file instead of saving it. The query asks the asset
              route for a Content-Disposition, which is the one instruction
              that survives the redirect, and it names the file from the asset
              row rather than from the URL. */}
          {raw ? (
            <a
              className="btn btn-ghost btn-sm shrink-0"
              href={`/assets/${raw.key}?download`}
              title={[
                raw.filename,
                raw.width && raw.height ? `${raw.width}×${raw.height}` : null,
                raw.durationSeconds ? timecode(raw.durationSeconds) : null,
                `${(raw.sizeBytes / (1024 * 1024)).toFixed(1)} MB`,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              {"Download raw recording"}
            </a>
          ) : null}
          <Link to={`/projects/${id}/editor`} className="btn btn-secondary shrink-0">
            Back to editor
          </Link>
        </div>

        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {cannotRender ? (
          <p role="status" className="panel px-4 py-3 text-[13px] text-signal-warn">
            {cannotRender}
          </p>
        ) : null}

        <div className="flex flex-wrap items-start gap-5">
          <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-3">
            <ExportPlayer choices={playerChoices(existing, languages, timeline?.meta.projectName ?? "Project")}>
              {here ? (
                <section className="border-b border-[var(--edge)] p-5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h2 className="text-[13px] font-semibold text-ink-100">
                      {here.uploading ? "Saving your video" : "Rendering your video"}
                    </h2>
                    <button
                      className="btn btn-ghost btn-sm shrink-0"
                      onClick={() => browserAbort.current?.abort()}
                      disabled={here.uploading}
                    >
                      {"Stop"}
                    </button>
                  </div>
                  {/* The one thing somebody needs to know, and only while it is
                      true: this render lives in the tab, so closing it ends the
                      render. Once the bytes are on their way up it does not, so
                      the warning goes away rather than being left to nag. */}
                  <ProgressLine
                    id={`browser-render-${browserRun}`}
                    label={here.uploading ? "Saving your video" : "Rendering your video"}
                    progress={here.progress}
                    eta={here.eta}
                    note={here.uploading ? "Almost done." : BROWSER_NOTE}
                    warn={!here.uploading}
                  />
                </section>
              ) : null}

              {status ? (
                <section className="border-b border-[var(--edge)] p-5">
                  <div className="mb-2 flex items-center justify-between">
                    {/* What is being made, not only how far along. A bare percentage
                        cannot answer "is this the 4K one I asked for?", and when a
                        second render is refused because this one is still going, the
                        screen goes on showing this one — which reads as the chosen
                        size having been quietly ignored. */}
                    <Eyebrow>
                      {status.status}
                      {status.width ? ` · ${status.width}×${status.height} · ${status.fps}fps` : ""}
                    </Eyebrow>
                  </div>
                  {status.status === "failed" ? (
                    <div className="h-1.5 overflow-hidden rounded-full bg-bay-700">
                      <div
                        className="h-full rounded-full bg-signal-bad transition-[width] duration-300"
                        style={{ width: `${Math.max(2, status.progress * 100)}%` }}
                      />
                    </div>
                  ) : (
                    <ProgressLine
                      id={status.jobId ?? `render-${status.id}`}
                      label={serverRenderLabel(status.status, status.step ?? null)}
                      progress={status.progress}
                      eta={running ? status.eta : null}
                      note={
                        running
                          ? emailNoteFor(jobs, status.jobId, account?.email ?? null, "your export is ready to download")
                          : null
                      }
                    />
                  )}
                  {status.error ? (
                    <div className="mt-3">
                      <ErrorNote>{status.error}</ErrorNote>
                    </div>
                  ) : null}
                  {running ? (
                    <div className="mt-3 flex items-center justify-end gap-3">
                      {/* A render is minutes at best and hours at worst, and until now
                          the only way out of one started by mistake — at the wrong
                          size, from the wrong cut — was to wait for it. Nothing else
                          can start while it runs. */}
                      <button
                        className="btn btn-ghost btn-sm shrink-0"
                        disabled={cancelling}
                        onClick={() => void cancel()}
                      >
                        {cancelling ? "Cancelling…" : "Cancel"}
                      </button>
                    </div>
                  ) : null}
                </section>
              ) : null}
            </ExportPlayer>
          </div>

          <ExportPanel
            tab={tab}
            onTab={setTab}
            live={shareLive}
            panels={{
              download: (
                <DownloadTab
                  projectId={id}
                  quality={
                    <>
                      {/* A grid rather than a wrapping row, so the cards line
                          up and read as one set; the sizes not offered yet are
                          one line under it rather than cards nobody can press. */}
                      <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Export size">
                        {quality.cards.map(({ option, index }) => (
                          <button
                            key={option.label}
                            type="button"
                            role="radio"
                            aria-checked={index === preset}
                            onClick={() => {
                              setPreset(index);
                              setPresetChosen(true);
                            }}
                            disabled={running || !isSelectable(option)}
                            className={`rounded-[10px] px-2.5 py-2 text-left transition-colors focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)] disabled:opacity-50 ${
                              index === preset
                                ? "border-[1.5px] border-ember-400 bg-[#FFF4EC]"
                                : "border border-[var(--edge)] bg-white hover:border-ember-500"
                            }`}
                          >
                            <span className="block text-[14px] font-semibold text-ink-100">
                              {option.label.split(" · ")[0]}
                            </span>
                            <span className="block text-[12px] font-medium text-ink-200">{`${option.fps} fps`}</span>
                            <span className="mt-0.5 block text-[11.5px] leading-snug text-ink-300">{option.use}</span>
                          </button>
                        ))}
                      </div>
                      {quality.comingSoon ? <p className="text-[12px] text-ink-300">{quality.comingSoon}</p> : null}
                      {timeline && chosen.fps < timeline.fps ? (
                        <p className="text-[12px] leading-relaxed text-signal-warn">
                          This project is {timeline.fps}fps. Exporting at {chosen.fps} throws away half its frames,
                          which shows on typing and scrolling.
                        </p>
                      ) : null}

                      {/* Upscaling is not free and buys nothing. The recording's own size
                          is the ceiling on detail; past it a bigger export is a bigger
                          file of the same picture. */}
                      {source?.width && chosen.width > source.width ? (
                        <p className="text-[12px] leading-relaxed text-signal-warn">
                          Your recording is {source.width}×{source.height}. A {chosen.width}×{chosen.height} export will
                          not add detail to it — the cards and captions are drawn sharper, the footage is only enlarged.
                        </p>
                      ) : null}

                      {chosen.width >= 3840 ? (
                        <p className="text-[12px] leading-relaxed text-ink-400">
                          4K is four times the pixels of 1080p. Your machine can encode it, which is why it is offered
                          here — expect it to take roughly four times as long, and keep the tab open while it does.
                        </p>
                      ) : null}

                      {quote?.billing ? (
                        <p className="text-[12.5px] text-ink-300">
                          {quote.freeForever
                            ? "Free. Nothing this team makes is charged."
                            : quote.alreadyPaid
                              ? "Already paid for — re-cuts are free, and rendering in your browser always is."
                              : quote.willUseFreeVideo
                                ? `This uses one of your free videos. ${quote.freeRemaining} of ${quote.freeVideoLimit} left.`
                                : quote.affordable
                                  ? `This export costs ${formatUsd(quote.priceUsd)}. You have ${formatUsd(quote.balanceUsd)}.`
                                  : `This export costs ${formatUsd(quote.priceUsd)} and you have ${formatUsd(quote.balanceUsd)}.`}
                          {!quote.affordable ? (
                            <>
                              {" "}
                              <Link to="/settings#billing" className="text-ember-600 underline">
                                Add credit
                              </Link>
                            </>
                          ) : null}
                        </p>
                      ) : null}

                      <ExportChoice
                        open={choosing}
                        preset={chosen}
                        canRenderHere={canEncodeChosen}
                        reasons={cannotEncodeBecause}
                        serverPriceUsd={renderFeeUsd}
                        formatUsd={formatUsd}
                        onCancel={() => setChoosing(false)}
                        onChoose={(where) => {
                          setChoosing(false);
                          void exportChecked(where);
                        }}
                      />
                    </>
                  }
                  videos={
                    <ExportRows
                      projectId={id}
                      projectName={timeline?.meta.projectName ?? "Project"}
                      rows={rows}
                      view={languages}
                      checked={queue}
                      onToggle={toggle}
                      busy={running || starting || languagesRunning}
                      canRender={canEncodeChosen}
                      runs={rowRuns}
                      english={existing ? { href: existing.downloadUrl, filename: existing.filename } : null}
                    />
                  }
                  actions={
                    <>
                      {languagesRunning && !here && !starting ? (
                        <button type="button" className="btn btn-ghost" onClick={() => languageAbort.current?.abort()}>
                          Stop
                        </button>
                      ) : null}
                      <button
                        className="btn btn-primary"
                        onClick={() => (englishChecked ? setChoosing(true) : void exportChecked(null))}
                        disabled={
                          running ||
                          starting ||
                          languagesRunning ||
                          !timeline ||
                          queue.length === 0 ||
                          (englishChecked && quote?.billing === true && !quote.affordable)
                        }
                      >
                        {buttonLabel}
                      </button>
                    </>
                  }
                  note={
                    queue.some((key) => key !== "")
                      ? "Languages render in this tab, free. Keep it open until they finish."
                      : null
                  }
                  zippable={(languages?.languages ?? []).some((one) => Object.values(one.downloads).some(Boolean))}
                  blocked={languages && !languages.canTranslate ? blockedMessage(languages.reason) : null}
                  history={<ExportHistory exports={exports} kept={EXPORTS_KEPT} />}
                />
              ),
              share: (
                <ShareSection
                  projectId={id}
                  projectName={timeline?.meta.projectName ?? "Project"}
                  kind="demo"
                  onLiveChange={setShareLive}
                  refreshKey={shareRefreshKey(existing?.url ?? null, languages)}
                />
              ),
            }}
          />
        </div>
      </div>
    </AppShell>
  );
};
